import { buildAccountKey } from '@sales-portal/domain/pricing/blocs/utils/account-key';
import { create } from 'zustand';

// Session-only optimistic mirror of the user's server-side bookmarks, keyed by
// accountKey (same key shape as the edits store). Server reads hydrate it; the
// mutator writes through it optimistically and rolls back on failure. It is NOT
// persisted — the server owns cross-session truth.

export interface BookmarkEntry {
  bookmarkedAt: string;
  note: string | null;
}

export interface HydrateBookmarkEntry extends BookmarkEntry {
  restaurantUUID: string;
  variantPackUUID: string;
}

interface PricingBookmarksState {
  bookmarks: Record<string, BookmarkEntry>;
  // Keys written locally that no server read has confirmed yet. A read issued
  // before the write — a cached one, most often — must not drop them.
  unconfirmedKeys: Set<string>;
}

interface PricingBookmarksStore extends PricingBookmarksState {
  // Replaces the set from an authoritative server read, keeping local writes
  // the read is too old to know about.
  hydrate: (entries: HydrateBookmarkEntry[]) => void;
  removeBookmark: (variantPackUUID: string, restaurantUUID: string) => void;
  // bookmarkedAt is optional: omit for a fresh optimistic add (keeps any existing
  // timestamp, else stamps now); pass it to restore an exact prior entry.
  setBookmark: (
    variantPackUUID: string,
    restaurantUUID: string,
    note: string | null,
    bookmarkedAt?: string,
  ) => void;
}

export const bookmarkKey = (
  variantPackUUID: string,
  restaurantUUID: string,
): string => buildAccountKey({ restaurantUUID, variantPackUUID });

export const usePricingBookmarksStore = create<PricingBookmarksStore>()(
  (set) => ({
    bookmarks: {},
    hydrate: (entries) =>
      set((state) => {
        const bookmarks = Object.fromEntries(
          entries.map((entry) => [
            bookmarkKey(entry.variantPackUUID, entry.restaurantUUID),
            { bookmarkedAt: entry.bookmarkedAt, note: entry.note },
          ]),
        );

        // A key stays unconfirmed until a read comes back carrying it; until
        // then the local entry survives, so the sidebar and the Bookmarked tab
        // can't flicker off a bookmark the user just made.
        const unconfirmedKeys = new Set<string>();
        state.unconfirmedKeys.forEach((key) => {
          if (key in bookmarks) {
            return;
          }
          const localEntry = state.bookmarks[key];
          if (localEntry) {
            bookmarks[key] = localEntry;
            unconfirmedKeys.add(key);
          }
        });

        return { bookmarks, unconfirmedKeys };
      }),

    removeBookmark: (variantPackUUID, restaurantUUID) =>
      set((state) => {
        const key = bookmarkKey(variantPackUUID, restaurantUUID);
        const bookmarks = { ...state.bookmarks };
        delete bookmarks[key];
        const unconfirmedKeys = new Set(state.unconfirmedKeys);
        unconfirmedKeys.delete(key);
        return { bookmarks, unconfirmedKeys };
      }),

    setBookmark: (variantPackUUID, restaurantUUID, note, bookmarkedAt) =>
      set((state) => {
        const key = bookmarkKey(variantPackUUID, restaurantUUID);
        return {
          bookmarks: {
            ...state.bookmarks,
            [key]: {
              bookmarkedAt:
                bookmarkedAt ??
                state.bookmarks[key]?.bookmarkedAt ??
                new Date().toISOString(),
              note,
            },
          },
          unconfirmedKeys: new Set(state.unconfirmedKeys).add(key),
        };
      }),

    unconfirmedKeys: new Set<string>(),
  }),
);

export const selectIsBookmarked = (
  state: PricingBookmarksState,
  variantPackUUID: string,
  restaurantUUID: string,
): boolean => bookmarkKey(variantPackUUID, restaurantUUID) in state.bookmarks;

export const selectBookmarkNote = (
  state: PricingBookmarksState,
  variantPackUUID: string,
  restaurantUUID: string,
): string | null =>
  state.bookmarks[bookmarkKey(variantPackUUID, restaurantUUID)]?.note ?? null;

export const selectBookmarkCount = (state: PricingBookmarksState): number =>
  Object.keys(state.bookmarks).length;
